import { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import type { Session } from '@supabase/supabase-js';
import { supabase } from '../lib/supabaseClient';
import { openedByEmailLink, sessionState, type SessionState } from './session';

/**
 * Who is signed in, and whether their session is still good.
 *
 * The state a long-lived tab actually reaches is "expired", not "signed out",
 * and the difference matters: somebody who has been working all day should be
 * told their session ran out and asked to sign in again, not dropped onto a
 * marketing page or, worse, left looking at a screen whose next click fails.
 */

export interface SessionContextValue {
    session: Session | null;
    state: SessionState;
    /** True until the first answer arrives, so nothing renders on a guess. */
    loading: boolean;
    userId: string | null;
    email: string | null;
    verified: boolean;
    /**
     * The session was opened by an emailed link (sign-up confirmation, reset), not the password.
     * Auth lets such a session set a password without the current one, so the screens end it as
     * soon as the link has done its job (docs/product/phase-0/03 §2).
     */
    openedByLink: boolean;
    /** Ends this browser's session only (Auth revokes it); every other device stays signed in. */
    endThisSession: () => Promise<void>;
    signOut: () => Promise<void>;
    /** Re-read from the client, after a flow that changes the session. */
    refresh: () => Promise<void>;
}

const SessionContext = createContext<SessionContextValue | null>(null);

export function SessionProvider({ children }: { children: React.ReactNode }) {
    const [session, setSession] = useState<Session | null>(null);
    const [loading, setLoading] = useState(true);
    // Recomputed on a timer as well as on events: a session does not announce
    // its own expiry, it simply becomes old.
    const [now, setNow] = useState(() => Date.now());

    useEffect(() => {
        let cancelled = false;
        const client = supabase();

        client.auth.getSession().then(({ data }) => {
            if (cancelled) return;
            setSession(data.session ?? null);
            setLoading(false);
        }).catch(() => {
            if (!cancelled) setLoading(false);
        });

        const { data: subscription } = client.auth.onAuthStateChange((_event, next) => {
            if (cancelled) return;
            setSession(next ?? null);
            setNow(Date.now());
            setLoading(false);
        });

        const tick = setInterval(() => setNow(Date.now()), 30_000);

        return () => {
            cancelled = true;
            clearInterval(tick);
            subscription.subscription.unsubscribe();
        };
    }, []);

    const signOut = useCallback(async () => {
        try {
            await supabase().auth.signOut();
        } finally {
            // Whatever the server said, this tab is done with that session.
            setSession(null);
        }
    }, []);

    const endThisSession = useCallback(async () => {
        try {
            await supabase().auth.signOut({ scope: 'local' });
        } finally {
            setSession(null);
        }
    }, []);

    const refresh = useCallback(async () => {
        const { data } = await supabase().auth.getSession();
        setSession(data.session ?? null);
        setNow(Date.now());
    }, []);

    const value = useMemo<SessionContextValue>(() => {
        const state = sessionState(session, now);
        return {
            session,
            state,
            loading,
            userId: session?.user?.id ?? null,
            email: session?.user?.email ?? null,
            verified: Boolean(session?.user?.email_confirmed_at),
            openedByLink: openedByEmailLink(session?.access_token),
            signOut,
            endThisSession,
            refresh,
        };
    }, [session, now, loading, signOut, endThisSession, refresh]);

    return <SessionContext.Provider value={value}>{children}</SessionContext.Provider>;
}

export function useSession(): SessionContextValue {
    const value = useContext(SessionContext);
    if (!value) throw new Error('useSession must be used inside a SessionProvider');
    return value;
}
